import { useMemo } from 'react';
import useFetch from 'use-http';
import type { Res } from 'use-http';
import { createFetchRequestApi } from './request-api';
import { createFetchResult } from './result';
import {
  createFetchRequestCacheKey,
} from './url';
import type {
  FetchBaseRequestApi,
  FetchResult,
  UseHttpCoreFetchOptions,
} from './types';

interface UseHttpResult<TData, TResponse> {
  request: FetchBaseRequestApi<TData>;
  response: TResponse | null | undefined;
  loading: boolean;
  data: TData | null | undefined;
  error: Error | null | undefined;
}

export function useHttpCoreFetch<TData = unknown, TResponse = Res<TData>>(options: UseHttpCoreFetchOptions): FetchResult<TData, TResponse> {
  const {
    baseUrl,
    requestOptions,
    dependencies,
    invalidateCacheOnMutation,
    throwOnError,
    transformResponse,
  } = options;
  const {
    request,
    response,
    loading,
    data,
    error,
  } = useFetch(
    baseUrl,
    requestOptions as Parameters<typeof useFetch>[1],
    dependencies ? [...dependencies] : undefined
  ) as unknown as UseHttpResult<TData, TResponse>;

  const wrappedRequest = useMemo(
    () => createFetchRequestApi(request, {
      getCacheKey: (
        method,
        route,
        body
      ) => createFetchRequestCacheKey(
        baseUrl,
        method,
        route,
        body
      ),
      invalidateCacheOnMutation,
      throwOnError,
      transformResponse,
    }),
    [
      baseUrl,
      invalidateCacheOnMutation,
      request,
      throwOnError,
      transformResponse,
    ]
  );

  return createFetchResult(
    wrappedRequest,
    response,
    loading,
    data,
    error
  );
}
